import { useWindowedAudioData } from '@remotion/media-utils'
import { useLayoutEffect, useMemo } from 'react'
import { useCurrentFrame, useDelayRender, useVideoConfig } from 'remotion'
import {
  NUMBER_OF_SAMPLES,
  expectedWindowCount,
  frequencyValues,
  isWindowCovered,
  loadedWindowCount,
  maxPositiveSample,
} from './frequencyScale'
import { mediaFrameAt, useMediaTimeline } from './mediaTimeline'
import { useWaveformPeak } from './useWaveformPeak'

/**
 * Frequency-domain data for the current frame, without downloading the source.
 *
 * ## Why this is not `useAudioData`
 *
 * `useAudioData` downloads the *entire* file and decodes it to PCM — around
 * 642 MB for a 28-minute recording — in every Lambda invocation, and again in
 * the browser for each of the ~15 `ClipScene`s the New Render dialog mounts. It
 * scales with the length of the *source*, not the length of the output, so a
 * 15-second render off a long podcast died with `Runtime.TruncatedResponse`.
 *
 * `useWindowedAudioData` range-fetches only the three 10-second windows around
 * the current frame through mediabunny/WebCodecs and evicts the rest, so
 * resident PCM is ~30 seconds regardless of source length and each Lambda
 * function touches only the windows its own frame range needs.
 *
 * ## Data flow
 *
 * ```
 *   renderChunk.ts (Trigger)                       browser
 *   resolveTranscriptionAudio → audioUrl           getRecording / getClip
 *   resolveWaveformUrl        → waveformUrl        resolvePreviewMedia
 *            │                                              │
 *            ▼  clipScenePropsSchema.parse                   ▼
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ clipScene { mediaUrl, audioUrl?, waveformUrl?, … }        │
 *   └──────────────────────────────────────────────────────────┘
 *            │  10 layouts, 12 call sites:
 *            │  mediaUrl={sceneAudioSrc(clipScene)} waveformUrl={…}
 *            ▼
 *   Bars / RadialBars / Hills / Wave
 *            ▼
 *   useFrequencyData(src, mediaStart, waveformUrl)
 *     ├─ useWindowedAudioData → audioData (windows [i-1, i, i+1]), offset
 *     ├─ useWaveformPeak      → whole-file peak | null
 *     ├─ coverage guard: hold a delayRender until f-1..f+1 (±512) are loaded
 *     ├─ visualizeAudio(512 samples, smoothing) → slice 256
 *     └─ rescaleFrequencyData(values, localMax, peak)
 *            ▼
 *   getBars → processAudioFftValue(minDb/maxDb) → Math.log(1 + x)
 * ```
 *
 * @param audioSrc    what to analyze — `sceneAudioSrc(clipScene)`, i.e. the
 *                    audio-only rendition when one exists, else the media file.
 * @param audioStart  offset of the scene into that media, in seconds. Used
 *                    only outside a `ClipScene`; inside one the scene's media
 *                    timeline (`mediaTimeline.ts`) maps frames, cuts included.
 * @param waveformUrl `waveform.dat` for the whole-file amplitude peak. Without
 *                    it the bars fall back to a window-local scale, which steps
 *                    at 10-second boundaries.
 */

/**
 * Fixed. `useWindowedAudioData` throws if it changes between renders, and it is
 * the unit `isWindowCovered` reasons about.
 */
const WINDOW_SECONDS = 10

// `NUMBER_OF_SAMPLES` (512) comes from `frequencyScale.ts`, which owns the FFT
// call. `getVisualization` reads twice that many samples per frame centred on
// it, so it reaches 512 samples either side — exactly the padding the coverage
// guard below needs.

export function useFrequencyData(
  audioSrc: string,
  audioStart = 0,
  waveformUrl?: string | null,
): number[] | null {
  const { fps } = useVideoConfig()
  const frame = useCurrentFrame()
  const { delayRender, continueRender } = useDelayRender()

  // Inside a `ClipScene` the media timeline decides which media frame this
  // output frame shows, cuts included (#89). Its single-piece answer is the
  // `frame + round(audioStart * fps)` used outside one.
  const timeline = useMediaTimeline()
  const shiftedFrame = timeline
    ? mediaFrameAt(timeline, frame)
    : frame + Math.round(audioStart * fps)

  const { audioData, dataOffsetInSeconds } = useWindowedAudioData({
    src: audioSrc,
    frame: shiftedFrame,
    fps,
    windowInSeconds: WINDOW_SECONDS,
  })

  const peak = useWaveformPeak(waveformUrl)

  const channel = audioData?.channelWaveforms[0]

  // `currentAudioData` is memoized inside the hook, so its identity is stable
  // for a given window set — one scan per window change, not one per frame.
  const localMax = useMemo(
    () => (channel ? maxPositiveSample(channel) : 0),
    [channel],
  )

  // Whether every sample this frame's visualization reads is actually loaded.
  //
  // `useWindowedAudioData` releases its own `delayRender` as soon as the
  // *current* window resolves and fetches the neighbours afterwards, so at a
  // window boundary the `f±1` smoothing terms would be computed against
  // whatever samples happened to sit next to them in the concatenated buffer.
  const covered =
    audioData != null && channel != null
      ? isWindowCovered({
          frame: shiftedFrame,
          fps,
          sampleRate: audioData.sampleRate,
          dataOffsetInSeconds,
          dataLength: channel.length,
          durationInSeconds: audioData.durationInSeconds,
          halfSampleSize: NUMBER_OF_SAMPLES,
        })
      : false

  // The safety valve. Holding a frame is only correct while a fetch is still
  // outstanding; if the hook has loaded everything it is ever going to load and
  // coverage is *still* false, waiting is a render timeout — the same symptom
  // as the OOM this change exists to fix. Take the boundary artifact instead.
  const moreWindowsComing =
    audioData != null &&
    channel != null &&
    loadedWindowCount({
      dataLength: channel.length,
      sampleRate: audioData.sampleRate,
      windowInSeconds: WINDOW_SECONDS,
    }) <
      expectedWindowCount({
        frame: shiftedFrame,
        fps,
        windowInSeconds: WINDOW_SECONDS,
        durationInSeconds: audioData.durationInSeconds,
      })

  const shouldWait = audioData != null && !covered && moreWindowsComing

  // Acquired in a layout effect and released in its cleanup, exactly like the
  // windowed hook's own guard. Calling `delayRender` from the render body would
  // leak a handle per render and hang the Lambda.
  useLayoutEffect(() => {
    if (!shouldWait) return

    const handle = delayRender(
      `Waiting for neighbouring audio windows of "${audioSrc}" to be loaded`,
    )
    return () => {
      continueRender(handle)
    }
  }, [shouldWait, audioSrc, delayRender, continueRender])

  if (!audioData || !channel) return null

  // `peak === undefined` is "still loading". Rendering here would flash one
  // frame on the window-local scale before the real one arrives.
  if (peak === undefined) return null

  if (shouldWait) return null

  // `getVisualization` throws below `numberOfSamples * 2`. A lone trailing
  // window can be shorter than that when a selection ends inside the last few
  // milliseconds of the file.
  if (channel.length < NUMBER_OF_SAMPLES * 2) return null

  // A silent window has no denominator: `maxInt` would be 0 and every
  // magnitude `NaN`.
  if (localMax <= 0) return new Array(NUMBER_OF_SAMPLES / 2).fill(0)

  // The FFT and the rescale live in `frequencyScale.ts` as one unit, so
  // `windowedParity.test.ts` asserts against the transform this hook actually
  // runs rather than a second assembly of the same calls.
  return frequencyValues({
    audioData,
    frame: shiftedFrame,
    fps,
    dataOffsetInSeconds,
    localMax,
    globalPeak: peak,
  })
}
